import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import "media-chrome";
import tippy from "tippy.js";
import { HorizontalScroller } from "./horizontalScroller.js";

gsap.registerPlugin(ScrollTrigger);

/* -------------------------------------------------- */
/* 🙂 General */
/* -------------------------------------------------- */

const mm = gsap.matchMedia();

gsap.defaults({
  duration: 0.25,
  ease: "power1.inOut",
});

const isReducedMotion = () => {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const isDesktop = () => {
  return window.matchMedia("(min-width: 1000px)").matches;
};

/* -------------------------------------------------- */
/* 👵 Check for old browsers */
/* -------------------------------------------------- */

if (
  typeof CSSLayerBlockRule !== "function" &&
  !localStorage.getItem("oldBrowserBanner")
) {
  // Add a banner to the page
  const banner = document.createElement("a");
  banner.style =
    "position:fixed;top:0;left:0;width:100%;padding:1rem;background:red;text-align:center;color:#fff;z-index:9999999;cursor:pointer";

  // Check language
  const lang = navigator.language || navigator.userLanguage;
  if (lang.startsWith("de")) {
    banner.innerHTML =
      "Sie verwenden einen veralteten Browser. Die Website kann nicht korrekt angezeigt werden.";
  } else {
    banner.innerHTML =
      "You are using an outdated browser. The website can not be displayed correctly.";
  }

  banner.addEventListener("click", (e) => {
    banner.remove();
    localStorage.setItem("oldBrowserBanner", "dismissed");
  });

  // Add the banner to the body
  document.body.appendChild(banner);
}

/* -------------------------------------------------- */
/* 📏 Guides */
/* -------------------------------------------------- */

// Create the guides
const guides = document.createElement("div");
guides.className = "guides wrap";
const guidesInner = document.createElement("div");
guidesInner.className = "inner grid";
guides.appendChild(guidesInner);
for (let i = 0; i < 12; i++) {
  const guide = document.createElement("div");
  guide.className = "guides__guide";
  guidesInner.appendChild(guide);
}
document.body.appendChild(guides);

// Set up a tween that animates the guides in and out
let toggleGuides = gsap
  .to(guides, {
    duration: 0.25,
    ease: "power2.inOut",
    autoAlpha: 1,
    paused: true,
  })
  .reverse();

// Check if the guides was enabled before
if (localStorage.getItem("debug") == "true") {
  toggleGuides.play().progress(1);
}

// Listen for ⌃G keypresses
document.addEventListener("keydown", (e) => {
  if (e.key === "g" && e.ctrlKey) {
    // Toggle the guides when ⌃G is pressed
    toggleGuides.reversed() ? toggleGuides.play() : toggleGuides.reverse();
    // Save the state in local storage
    localStorage.setItem("debug", !toggleGuides.reversed());
  }
});

guides.dataset.windowWidth = window.innerWidth;

window.addEventListener("resize", () => {
  guides.dataset.windowWidth = window.innerWidth;
});

/* -------------------------------------------------- */
/* ↔️ Horizontal Scrollers */
/* -------------------------------------------------- */

document.addEventListener("DOMContentLoaded", () => {
  new HorizontalScroller({
    elements: document.querySelectorAll(".horizontal-scroller"),
    autoHeight: true,
  });

  // Set initial header height
  updateHeaderHeight();
});

// Update header height on resize
window.addEventListener("resize", () => {
  updateHeaderHeight();
});

/* -------------------------------------------------- */
/* 💆‍♂️ Header */
/* -------------------------------------------------- */

// Function to calculate and set header bottom offset (where menu starts)
const updateHeaderHeight = () => {
  const header = document.querySelector(".header");
  if (!header) return;

  // Get the bottom position of the header relative to viewport
  const headerRect = header.getBoundingClientRect();
  const headerBottom = headerRect.bottom;

  document.documentElement.style.setProperty(
    "--height-header",
    `${headerBottom}px`
  );
};

mm.add("(min-width: 1000px)", () => {
  // Common values
  const logoText = document.querySelector(".header .logo__svg--text");
  const logoSignet = document.querySelector(".header .logo__svg--signet");
  const distance = 93 + 25;

  // Animation values
  const headerY = "-3.5rem";
  const headerBoxShadow = "0px 2px 5px 0px rgba(0, 0, 0, 0.15)";
  const logoNavY = "1.75rem";
  const c2aYPercent = 0;
  const logoSignetProps = {
    rotate: -90,
    x: -distance,
    autoAlpha: 0,
    scale: 0.7,
  };
  const logoTextProps = {
    x: -distance,
    transformOrigin: "left center",
    scale: 0.7,
  };

  // ScrollTrigger start function
  const getScrollStart = () => {
    if (document.querySelector(".banner")) {
      return 45;
    }
    return 0;
  };

  // Alternative animation for users who prefer reduced motion
  if (isReducedMotion()) {
    let isCompact = false;

    const compactHeader = () => {
      if (!isCompact) {
        gsap.set(".header", {
          y: headerY,
          boxShadow: headerBoxShadow,
        });
        gsap.set(".header__logo, .header__navigation", {
          y: logoNavY,
        });
        gsap.set(".header__c2a", {
          yPercent: c2aYPercent,
        });

        if (logoSignet) {
          gsap.set(logoSignet, logoSignetProps);
        }
        if (logoText) {
          gsap.set(logoText, logoTextProps);
        }

        isCompact = true;
        updateHeaderHeight();
      }
    };

    const expandHeader = () => {
      if (isCompact) {
        gsap.set(
          ".header, .header__logo, .header__navigation, .header__c2a, .header .logo__svg--signet, .header .logo__svg--text",
          {
            clearProps: "all",
          }
        );
        isCompact = false;
        updateHeaderHeight();
      }
    };

    ScrollTrigger.create({
      trigger: ".header",
      start: getScrollStart,
      end: ScrollTrigger.maxScroll(window),
      onToggle: (self) => {
        if (self.isActive) {
          compactHeader();
        } else {
          expandHeader();
        }
      },
    });

    return () => {};
  }

  const headerTl = gsap.timeline({
    defaults: {
      ease: "power1.out",
    },
    scrollTrigger: {
      trigger: ".header",
      start: getScrollStart,
      end: "+=150",
      scrub: 0.25,
      invalidateOnRefresh: true,
      onUpdate: () => {
        updateHeaderHeight();
      },
    },
  });

  headerTl
    .to(
      ".header",
      {
        y: headerY,
        boxShadow: headerBoxShadow,
      },
      0
    )
    .to(
      ".header__logo, .header__navigation",
      {
        y: logoNavY,
      },
      0
    )
    .to(
      ".header__c2a",
      {
        yPercent: c2aYPercent,
      },
      0
    )
    .to(logoSignet, logoSignetProps, 0)
    .to(logoText, logoTextProps, 0);

  return () => {};
});

/* -------------------------------------------------- */
/* 🔤 Logo links */
/* -------------------------------------------------- */

const logoLinksContainers = document.querySelectorAll(".logo-links");

logoLinksContainers.forEach((container) => {
  const viewport = container.querySelector(".logo-links__viewport");
  const itemsWrapper = container.querySelector(".logo-links__items");

  if (isReducedMotion()) {
    gsap.set(viewport, {
      overflowX: "auto",
      paddingInline: "var(--spacing-inline)",
    });
  } else {
    if (itemsWrapper.scrollWidth > container.clientWidth) {
      const clones = [];
      for (let i = 0; i < 10; i++) {
        const clone = itemsWrapper.cloneNode(true);
        clone.setAttribute("aria-hidden", true);

        let cloneLinks = clone.querySelectorAll("a");
        cloneLinks.forEach((link) => {
          link.setAttribute("tabindex", -1);
        });

        viewport.appendChild(clone);
        clones.push(clone);
      }

      // Set up the animation
      const totalWidth = itemsWrapper.scrollWidth;
      const duration = totalWidth / 50;

      let animation = gsap.to([itemsWrapper, ...clones], {
        xPercent: -100,
        duration: duration,
        repeat: -1,
        ease: "none",
        modifiers: {
          xPercent: gsap.utils.wrap(-600, 0),
        },
      });

      // Start the animation from the middle
      animation.progress(0.5);

      container.addEventListener("mouseenter", function () {
        gsap.to(animation, {
          timeScale: 0,
          duration: 0.5,
          ease: "power1.inOut",
        });
      });

      container.addEventListener("mouseleave", function () {
        gsap.to(animation, {
          timeScale: 1,
          duration: 1,
          ease: "power1.inOut",
        });
      });
    }
  }
});

/* -------------------------------------------------- */
/* 🪗 Accordions */
/* -------------------------------------------------- */

const accordionItems = document.querySelectorAll(".menu-accordion-item");

const openAccordionItem = (item) => {
  const button = item.querySelector(".menu-accordion-item__title");
  const content = item.querySelector(".menu-accordion-item__content");
  const icon = button.querySelector(".menu-accordion-title__icon");

  button.setAttribute("aria-expanded", "true");
  content.setAttribute("aria-hidden", "false");

  gsap.to(content, {
    height: "auto",
    duration: isReducedMotion() ? 0 : 0.5,
    ease: "power1.inOut",
    onComplete: () => gsap.set(content, { height: "auto" }),
  });
  gsap.to(icon, { rotation: 45 });

  item.scrollIntoView({ behavior: "smooth", block: "nearest" });
};

const closeAccordionItem = (item) => {
  const button = item.querySelector(".menu-accordion-item__title");
  const content = item.querySelector(".menu-accordion-item__content");
  const icon = button.querySelector(".menu-accordion-title__icon");

  button.setAttribute("aria-expanded", "false");
  content.setAttribute("aria-hidden", "true");

  gsap.to(content, {
    height: 0,
    duration: isReducedMotion() ? 0 : 0.5,
    ease: "power1.inOut",
  });
  gsap.to(icon, { rotation: 0 });
};

const updateUrl = (id = null) => {
  if (id) {
    history.pushState(null, null, `#${id}`);
  } else {
    const canonicalUrl = document.querySelector("link[rel='canonical']")?.href;
    history.pushState(null, null, canonicalUrl || window.location.pathname);
  }
};

accordionItems.forEach((item) => {
  const button = item.querySelector(".menu-accordion-item__title");
  const content = item.querySelector(".menu-accordion-item__content");
  const icon = button.querySelector(".menu-accordion-title__icon");

  gsap.set(content, { height: 0, overflow: "hidden", display: "block" });
  gsap.set(icon, { rotation: 0 });

  button.addEventListener("click", () => {
    const isExpanded = button.getAttribute("aria-expanded") === "true";

    if (!isExpanded) {
      updateUrl(content.id);
      openAccordionItem(item);
    } else {
      updateUrl();
      closeAccordionItem(item);
    }
  });
});

window.addEventListener("load", () => {
  const hash = window.location.hash.slice(1);
  if (hash) {
    const targetItem = document.getElementById(hash);
    if (targetItem) {
      const targetAccordion = targetItem.closest(".menu-accordion-item");
      if (targetAccordion) {
        openAccordionItem(targetAccordion);
      }
    }
  } else {
    accordionItems.forEach(closeAccordionItem);
  }
});

/* -------------------------------------------------- */
/* 📱 Menu Dialog */
/* -------------------------------------------------- */

const menuToggle = document.querySelector(".menu-toggle");
const menu = document.querySelector(".menu");
const menuInner = document.querySelector(".menu__inner");

if (menuToggle && menu && menuInner) {
  const hamburgerPaths = menuToggle.querySelectorAll(".menu-toggle__icon path");
  let isMenuOpen = false;

  const openMenu = () => {
    isMenuOpen = true;
    menuToggle.setAttribute("aria-expanded", "true");
    menuToggle.setAttribute("aria-label", "Menü schließen");

    // Update header height before opening menu
    updateHeaderHeight();

    const headerC2a = document.querySelector(".header__c2a");

    // Hide header CTA
    if (headerC2a) {
      gsap.to(headerC2a, {
        autoAlpha: 0,
        duration: isReducedMotion() ? 0 : 0.25,
        ease: "power2.inOut",
      });
    }

    // Fade in menu container
    gsap.fromTo(
      menu,
      {
        autoAlpha: 0,
      },
      {
        autoAlpha: 1,
        duration: isReducedMotion() ? 0 : 0.25,
        ease: "power2.inOut",
      }
    );

    // Animate hamburger to cross
    if (hamburgerPaths.length >= 3) {
      const duration = isReducedMotion() ? 0 : 0.3;

      // Top line: rotate 45deg and move to center
      gsap.to(hamburgerPaths[0], {
        rotation: 45,
        transformOrigin: "center center",
        y: 6.3,
        duration: duration,
        ease: "power2.inOut",
      });

      // Middle line: fade out
      gsap.to(hamburgerPaths[1], {
        scaleX: 0,
        transformOrigin: "center center",
        duration: isReducedMotion() ? 0 : 0.2,
        ease: "power2.inOut",
      });

      // Bottom line: rotate -45deg and move to center
      gsap.to(hamburgerPaths[2], {
        rotation: -45,
        transformOrigin: "center center",
        y: -6.3,
        duration: duration,
        ease: "power2.inOut",
      });
    }
  };

  const closeMenu = () => {
    isMenuOpen = false;
    menuToggle.setAttribute("aria-expanded", "false");
    menuToggle.setAttribute("aria-label", "Menü öffnen");

    const headerC2a = document.querySelector(".header__c2a");

    // Show header CTA again
    if (headerC2a) {
      gsap.to(headerC2a, {
        autoAlpha: 1,
        duration: isReducedMotion() ? 0 : 0.2,
        ease: "power2.inOut",
      });
    }

    // Fade out menu container
    gsap.to(menu, {
      autoAlpha: 0,
      duration: isReducedMotion() ? 0 : 0.3,
      ease: "power2.inOut",
    });

    // Animate cross back to hamburger
    if (hamburgerPaths.length >= 3) {
      // Reset all transformations
      gsap.to(hamburgerPaths, {
        rotation: 0,
        y: 0,
        scaleX: 1,
        duration: isReducedMotion() ? 0 : 0.3,
        ease: "power2.inOut",
      });
    }
  };

  // Toggle button click handler
  menuToggle.addEventListener("click", () => {
    if (isMenuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  });
}

/* -------------------------------------------------- */
/* 🎈 Popups */
/* -------------------------------------------------- */

const popup = document.querySelector(".popup");

if (popup) {
  const closeButton = popup.querySelector(".popup__close-button");

  // Let's generate an ID for the popup so if it changes, it will show again
  const popupId = popup.textContent.replace(/\s/g, "").slice(0, 50);
  const localStoragePopupState = localStorage.getItem("popup-" + popupId);

  if (localStoragePopupState !== "closed") {
    popup.showModal();
  }

  const closePopup = () => {
    popup.close();
    localStorage.setItem("popup-" + popupId, "closed");
  };

  // Close on click on the close button
  closeButton.addEventListener("click", () => {
    closePopup();
  });

  // Close on click outside/backdrop
  popup.addEventListener("click", (e) => {
    if (e.target === popup) {
      closePopup();
    }
  });
}

/* -------------------------------------------------- */
/* 🗾 Map */
/* -------------------------------------------------- */

let maps = document.querySelectorAll(".map");

// Global promise to track Mapbox GL JS loading
let mapboxGLPromise = null;

const loadMapboxGL = () => {
  if (mapboxGLPromise) return mapboxGLPromise;

  mapboxGLPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js";
    script.onload = () => {
      if (window.mapboxgl) {
        resolve(window.mapboxgl);
      } else {
        reject(new Error("Mapbox GL JS failed to load"));
      }
    };
    script.onerror = () =>
      reject(new Error("Failed to load Mapbox GL JS script"));
    document.head.appendChild(script);
  });

  return mapboxGLPromise;
};

// Load Mapbox GL JS on page load
if (maps.length) {
  loadMapboxGL();
}

const initMap = async (mapEl) => {
  try {
    const mapboxgl = await loadMapboxGL();

    let mapData = JSON.parse(mapEl.dataset.map);

    mapboxgl.accessToken = mapData.accessToken;

    let map = new mapboxgl.Map({
      container: mapEl,
      style: mapData.styleUrl,
      center: [mapData.center.lon, mapData.center.lat],
      zoom: mapData.zoom,
      pitch: 30,
    });

    let bounds = new mapboxgl.LngLatBounds();

    let places = mapData.collection;

    places.features.forEach(function (marker) {
      var el = document.createElement("div");
      el.id = marker.properties.title;
      el.className = "poi " + marker.properties.class;
      el.style = marker.properties.style;
      el.setAttribute("data-lat", marker.properties.lat);
      el.setAttribute("data-lon", marker.properties.lon);
      new mapboxgl.Marker(el, {
        anchor: "bottom",
      })
        .setLngLat(marker.geometry.coordinates)
        .addTo(map);
      bounds.extend(marker.geometry.coordinates);
    });

    map.scrollZoom.disable();
    map.dragRotate.disable();

    map.on("load", () => {
      map.resize();
    });

    return map;
  } catch (error) {
    console.error("Error initializing map:", error);
    mapEl.textContent = "Failed to load map";
  }
};

maps.forEach((mapEl) => {
  let module = mapEl.closest(".module");
  let map;

  // Setup intersection observer to init map when it enters viewport
  let observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          initMap(mapEl)
            .then((initializedMap) => {
              if (initializedMap) {
                map = initializedMap;
                setupMapInteractions(module, map);
              }
            })
            .catch((error) => {
              console.error("Error in map initialization:", error);
            });
          observer.unobserve(mapEl);
        }
      });
    },
    {
      rootMargin: "100px",
      threshold: 0.1,
    }
  );

  observer.observe(mapEl);
});

function setupMapInteractions(module, map) {
  // POI clicks
  module.addEventListener("click", function (e) {
    if (e.target.matches(".poi, .poi *")) {
      let poi = e.target.closest(".poi") || e.target;
      const id = poi.id;

      module
        .querySelectorAll(".poi")
        .forEach((el) => el.classList.remove("active"));
      module
        .querySelectorAll(".place-headlines__item")
        .forEach((el) => (el.ariaCurrent = "false"));

      poi.classList.add("active");
      module.querySelector(
        `.place-headlines__item[data-id="${id}"]`
      ).ariaCurrent = "true";

      map.flyTo({
        center: [poi.dataset.lon, poi.dataset.lat],
        zoom: 17,
        speed: 0.5,
      });
    }
  });

  // Fly buttons
  const flyButtons = module.querySelectorAll(".fly-buttons__item");
  flyButtons.forEach((flyButton) => {
    flyButton.addEventListener("click", function () {
      if (flyButtons.length > 1) {
        flyButtons.forEach((el) => {
          el.ariaCurrent = el === flyButton ? "false" : "true";
        });
      }

      map.flyTo({
        center: [this.dataset.lon, this.dataset.lat],
        zoom: Number(this.dataset.zoom),
        speed: 0.5,
      });
    });
  });

  // Zoom in handler
  module
    .querySelector(".zoom-buttons__item--in")
    .addEventListener("click", () => {
      map.zoomIn({ duration: 1000 });
    });

  // Zoom out handler
  module
    .querySelector(".zoom-buttons__item--out")
    .addEventListener("click", () => {
      map.zoomOut({ duration: 1000 });
    });
}

/* -------------------------------------------------- */
/* 📌 Tooltips */
/* -------------------------------------------------- */

let tooltips = document.querySelectorAll("[data-tooltip]");

tooltips.forEach((tooltip) => {
  tippy(tooltip, {
    content: tooltip.dataset.tooltip,
    theme: "custom",
  });
});

/* -------------------------------------------------- */
/* 📰 Newsletter form */
/* -------------------------------------------------- */

const newsletterFormModule = document.querySelector(".module--newsletter");

if (newsletterFormModule) {
  const thanks = newsletterFormModule.querySelector(".form-wrap__thanks");
  const formWrap = newsletterFormModule.querySelector(".form-wrap__form");
  const form = newsletterFormModule.querySelector("form");

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    const message = document.createElement("div");
    message.style.cssText =
      "position:fixed; top:20px; right:20px; padding:20px; background:white; box-shadow:0 2px 10px rgba(0,0,0,0.1);";

    try {
      await fetch(e.target.action, {
        method: "POST",
        mode: "no-cors",
        body: new FormData(e.target),
      });

      formWrap.style.display = "none";
      thanks.style.display = "block";
    } catch (e) {
      console.log(e);
      alert("Fehler beim Senden des Formulars.");
    }

    document.body.appendChild(message);
    setTimeout(() => message.remove(), 3000);
  });
}

document.addEventListener("DOMContentLoaded", function () {
  // Check if we're on hotel-riesengebirge.de
  if (window.location.hostname !== "hotel-riesengebirge.de") return;

  const waitForUC = setInterval(function () {
    if (window.UC_UI) {
      clearInterval(waitForUC);
      window.UC_UI.updatePrivacyButtonSettings({
        position: "bottom-right",
        offsetX: 10,
        offsetY: 10,
      });
    }
  }, 500);
});

/* -------------------------------------------------- */
/* ⏸️ Pause buttons */
/* -------------------------------------------------- */

const pauseButtons = document.querySelectorAll(".pause-button");

pauseButtons.forEach((button) => {
  const video = button.parentElement.querySelector("video");

  button.addEventListener("click", (event) => {
    if (video.paused) {
      video.play();
      button.setAttribute("aria-pressed", "false");
    } else {
      video.pause();
      button.setAttribute("aria-pressed", "true");
    }

    event.preventDefault();
    event.stopPropagation();
  });
});

/* -------------------------------------------------- */
/* 📊 Table scroll shadows */
/* -------------------------------------------------- */

const tableContainers = document.querySelectorAll(".table-container");

tableContainers.forEach((container) => {
  const tableWrap = container.querySelector(".table-wrap");
  const firstColumn = container.querySelector(".table__column:first-child");

  const updateShadows = () => {
    // Calculate sticky column width
    if (firstColumn) {
      const columnWidth = firstColumn.offsetWidth;
      container.style.setProperty("--sticky-column-width", `${columnWidth}px`);
    }

    const isScrolledLeft = tableWrap.scrollLeft > 0;
    const isScrolledRight =
      tableWrap.scrollLeft < tableWrap.scrollWidth - tableWrap.clientWidth;

    container.classList.toggle("scroll-left", isScrolledLeft);
    container.classList.toggle("scroll-right", isScrolledRight);
  };

  // Initial check
  updateShadows();

  // Update on scroll
  tableWrap.addEventListener("scroll", updateShadows);

  // Update on resize (in case table changes size)
  window.addEventListener("resize", updateShadows);
});
